import { type KeyboardEvent, useRef } from 'react'
import { observer } from 'mobx-react-lite'
import classnames from 'classnames'
import dayjs from 'dayjs'
import { type DraggableProvided } from 'react-beautiful-dnd'
import {
  InputNumeric,
  InputText,
  makeTextFieldActionTooltip,
  makeTextFieldClear,
  makeTextFieldInteractions,
  makeTextFieldTooltip,
  TextField,
} from 'shared/ui/TextField'
import { DatePickerTextField } from 'shared/ui'
import { DatetimePickerTextField } from 'shared/ui/DatePicker/ui/DatetimePickerTextField/DatetimePickerTextField'
import { uiStore } from 'shared/store/uiStore'
import { getAriaLabel } from 'shared/lib'
import { type ICustomFieldType } from 'entities/CustomField'
import { type IResponseCustomField } from 'entities/Contacts/api/types'
import type { ContactInfoFormStore } from 'widgets/ContactsInfoForm'
import styles from '../styles.module.scss'
import { CustomFieldLabel } from './CustomFieldLabel'

type ICustomFieldField = {
  customField: IResponseCustomField
  store: ContactInfoFormStore
  ariaLabel?: string
  dragHandleProps?: DraggableProvided['dragHandleProps']
}

export const CustomFieldField = observer(
  ({ customField, store, ariaLabel, dragHandleProps }: ICustomFieldField) => {
    const {
      isVariantCreateContactModal,
      errors,
      updatingFields,
      handleSaveCustomField,
      handleUpdateCustomField,
      contact,
      zIndex,
    } = store
    const customFieldInputRef = useRef<HTMLInputElement>(null)

    if (!contact) return null

    const handleCustomFieldKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault()
        customFieldInputRef.current?.blur()
      }
      if (event.key === 'Escape') {
        const initialCustomFieldValue =
          contact.customFields.find((field) => field.field_key === customField.field_key)?.value
            ?.value ?? ''

        event.preventDefault()
        handleUpdateCustomField(customField, initialCustomFieldValue, true)
        customFieldInputRef.current?.blur()
      }
    }

    const customFieldType = (fieldType: ICustomFieldType | undefined) => {
      switch (fieldType) {
        case 'number':
          return InputNumeric
        default:
          return InputText
      }
    }

    const getInputField = () => {
      if ((customField.type || customField.value?.type) === 'date') {
        return (
          <span
            className={classnames(styles.value, {
              [styles.modalView]: isVariantCreateContactModal,
            })}
            aria-label={getAriaLabel(ariaLabel || 'Field', 'Value')}
          >
            <DatePickerTextField
              popperPlacement='bottom-end'
              placeholder={customField.name}
              dateFormat='MMM d, yyyy'
              date={customField.value?.value ? dayjs(customField.value?.value).toDate() : null}
              handleClose={() => {
                setTimeout(() => handleSaveCustomField(customField, contact), 50)
              }}
              handleDate={(value) =>
                handleUpdateCustomField(customField, value ? dayjs(value).format('YYYY-MM-DD') : '')
              }
              withCopy={Boolean(customField.value?.value)}
              variant='outline'
              size='small'
              zIndex={zIndex}
            />
          </span>
        )
      }
      if ((customField.type || customField.value?.type) === 'datetime') {
        return (
          <span
            className={classnames(styles.value, {
              [styles.modalView]: isVariantCreateContactModal,
            })}
            aria-label={getAriaLabel(ariaLabel || 'Field', 'Value')}
          >
            <DatetimePickerTextField
              withUiOffset
              date={
                customField.value?.value ? uiStore.dayjs(customField.value?.value).toDate() : null
              }
              handleDate={(value) =>
                handleUpdateCustomField(
                  customField,
                  value ? uiStore.dayjs(value).utc().toISOString() : ''
                )
              }
              dropdownProps={{
                onClose: () => {
                  setTimeout(() => handleSaveCustomField(customField, contact), 50)
                },
              }}
              textFieldProps={{
                variant: 'outline',
                size: 'small',
                readOnly: true,
                noDisabledWhenReadOnly: true,
                InputProps: { placeholder: customField.name },
                rightActions: [
                  makeTextFieldInteractions({
                    type: 'copy',
                    active: Boolean(customField.value?.value),
                    zIndex,
                  }),
                ],
              }}
            />
          </span>
        )
      }
      return (
        <TextField
          className={classnames(styles.value, {
            [styles.modalView]: isVariantCreateContactModal,
          })}
          size='small'
          variant='outline'
          Input={customFieldType(customField.type || customField.value?.type)}
          InputProps={{
            placeholder: customField.name,
            ref: customFieldInputRef,
            onKeyDown: handleCustomFieldKeyDown,
          }}
          value={customField.value?.value || ''}
          onChange={(value) => handleUpdateCustomField(customField, value, true)}
          onBlur={() => {
            handleSaveCustomField(customField, contact)
          }}
          readOnly={updatingFields.has(customField.field_key)}
          rightActions={[
            makeTextFieldInteractions(
              { type: 'copy', active: Boolean(customField.value?.value), zIndex: zIndex },
              {
                type: 'url',
                active:
                  (customField.type || customField.value?.type) === 'url' &&
                  Boolean(customField.value?.value),
                zIndex: zIndex,
              }
            ),
            makeTextFieldClear(),
          ]}
          mainActions={[
            makeTextFieldTooltip({ zIndex: zIndex }),
            makeTextFieldActionTooltip({
              color: 'red',
              placement: 'left',
              active: errors.has(customField.field_key),
              message: errors.get(customField.field_key),
              onClick: () => errors.delete(customField.field_key),
              zIndex: zIndex,
            }),
          ]}
          ariaLabel={getAriaLabel(ariaLabel || 'Field', 'Value')}
        />
      )
    }

    return (
      <div className={styles.field} key={customField.field_key} aria-label={ariaLabel}>
        <CustomFieldLabel customField={customField} dragHandleProps={dragHandleProps} />
        <div className={styles.fieldContent}>{getInputField()}</div>
      </div>
    )
  }
)
